import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../utils/api';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState('add');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
  });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const navigate = useNavigate();

  // 카테고리 목록 불러오기
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await api.get('/categories');
      setCategories(res.data);
      setError(null);
      setHasOrderChanged(false);
    } catch (err) {
      setError(err.response?.data?.message || '카테고리 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 폼 데이터 초기화
  const resetFormData = () => {
    setFormData({
      name: '',
    });
  };

  // 다이얼로그 열기
  const handleOpenDialog = (action, category = null) => {
    setDialogAction(action);
    
    if (action === 'edit' && category) {
      setSelectedCategory(category);
      setFormData({
        name: category.name || '',
      });
    } else {
      resetFormData();
    }
    
    setOpenDialog(true);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetFormData();
  };

  // 카테고리 추가 또는 수정 폼 제출
  const handleSubmit = async () => {
    try {
      if (dialogAction === 'add') {
        // 카테고리 추가
        await api.post('/categories', formData);
      } else {
        // 카테고리 수정
        await api.put(`/categories/${selectedCategory.id}`, formData);
      }
      
      // 다이얼로그 닫고 목록 새로고침
      handleCloseDialog();
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.message || '저장하는 중 오류가 발생했습니다.');
    }
  };

  // 폼 입력 변경
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // 삭제 확인 다이얼로그 열기
  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteConfirm(true);
  };

  // 카테고리 삭제
  const handleDelete = async () => {
    try {
      await api.delete(`/categories/${categoryToDelete.id}`);
      setDeleteConfirm(false);
      fetchCategories();
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || '삭제하는 중 오류가 발생했습니다.');
    }
  };

  // 드래그 종료 시 호출될 함수
  const handleDragEnd = (result) => {
    // 드래그가 취소된 경우
    if (!result.destination) {
      return;
    }

    // 순서가 변경되지 않은 경우
    if (result.destination.index === result.source.index) {
      return;
    }

    // 카테고리 목록 순서 업데이트
    const reorderedCategories = Array.from(categories);
    const [removed] = reorderedCategories.splice(result.source.index, 1);
    reorderedCategories.splice(result.destination.index, 0, removed);

    setCategories(reorderedCategories);
    setHasOrderChanged(true);
  };

  // 변경된 순서 저장
  const saveOrder = async () => {
    try {
      setLoading(true);
      // 서버에 필요한 ID와 sort_order만 전송
      const orderedIds = categories.map((category, index) => ({
        id: category.id
      }));
      
      await api.put('/categories/reorder', { categories: orderedIds });
      setHasOrderChanged(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || '순서 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1">
          구분 관리
        </Typography>
        <Box>
          {hasOrderChanged && (
            <Tooltip title="변경된 순서 저장">
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={saveOrder}
                sx={{ mr: 1 }}
              >
                순서 저장
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            구분 추가
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 0, width: '100%' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="categories">
              {(provided) => (
                <List
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {categories.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="등록된 구분이 없습니다." />
                    </ListItem>
                  ) : (
                    categories.map((category, index) => (
                      <Draggable 
                        key={category.id.toString()} 
                        draggableId={category.id.toString()} 
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              background: snapshot.isDragging ? '#f1f1f1' : 'transparent'
                            }}
                          >
                            <ListItem
                              sx={{ 
                                cursor: 'default',
                                borderLeft: snapshot.isDragging ? '3px solid #1976d2' : 'none',
                                bgcolor: snapshot.isDragging ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                              }}
                              secondaryAction={
                                <Box>
                                  <IconButton
                                    edge="end"
                                    aria-label="수정"
                                    onClick={() => handleOpenDialog('edit', category)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    edge="end"
                                    aria-label="삭제"
                                    onClick={() => handleDeleteClick(category)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              }
                            >
                              <Box 
                                {...provided.dragHandleProps} 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  mr: 2,
                                  cursor: 'grab',
                                  color: 'action.active',
                                  '&:hover': {
                                    color: 'primary.main'
                                  }
                                }}
                              >
                                <DragIndicatorIcon fontSize="medium" />
                              </Box>
                              <ListItemText primary={category.name} />
                            </ListItem>
                            <Divider component="li" />
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Paper>

      {/* 구분 추가/수정 다이얼로그 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogAction === 'add' ? '새 구분 추가' : '구분 수정'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="구분 이름"
            fullWidth
            required
            value={formData.name}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSubmit} variant="contained">
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>구분 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            '{categoryToDelete?.name}' 구분을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            이 구분을 사용하는 모든 고객의 구분 정보가 제거됩니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>취소</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories; 